import PropTypes from 'prop-types';
import React, { lazy, Suspense } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import Immutable from 'immutable';
import classNames from 'classnames';
import { Flipper, Front, Back } from 'components/Overview/Flipper';
import { Body, Heading, Wrapper } from 'components/Component';
import JawsComponent from 'components/JawsComponent';
import PeriodSelector from './PeriodSelector';
import HelpText from 'components/HelpText';
import Suggest from 'components/Suggest';
import Instruments from './Instruments';
import Instrument from './Instrument';
import Chart from './Chart';
import MapTab from './MapTab';

import * as ComponentActions from 'actions/component';
import * as WatchlistActions from 'actions/watchlist';
import * as GraphdataActions from 'actions/graphdata';
import './OverviewChart.scss';

const IndexMap = lazy(() => import('./Map'));

const componentId = 'OverviewChart';
const WATCHLIST_NAME = 'OVERVIEW_CHART';

class OverviewChart extends JawsComponent {
  spec = {
    type: 'secure/watchlist',
    columns:
      'SHORT_NAME,LAST,ITEM,ITEM_SECTOR,LONG_NAME,CHANGE_PCT,' +
      'CLOSENZ_DIV as CLOSE, order',
    watchlistName: WATCHLIST_NAME,
    userId: this.props.userId,
  };

  state = {
    activeKey: null,
    showMap: true,
    flipped: false,
    addItem: false,
  };

  static propTypes = {
    jaws: PropTypes.object,
    items: PropTypes.object,
    componentId: PropTypes.string,
    userId: PropTypes.string.isRequired,
    initialized: PropTypes.bool,
  };

  constructor(props) {
    super(props);

    const { dispatch } = this.props;
    this.actions = bindActionCreators(ComponentActions, dispatch);
    this.watchlistActions = bindActionCreators(WatchlistActions, dispatch);
    this.graphdataActions = bindActionCreators(GraphdataActions, dispatch);

    this.handleSelect = this.handleSelect.bind(this);
    this.handleClickAdd = this.handleClickAdd.bind(this);
    this.handleAddCancel = this.handleAddCancel.bind(this);
    this.toggleEditMode = this.toggleEditMode.bind(this);
    this.handleAddSelect = this.handleAddSelect.bind(this);
    this.handleSelectMap = this.handleSelectMap.bind(this);

    this.handleInstrumentMoveLeft = this.handleInstrumentMoveLeft.bind(this);
    this.handleInstrumentMoveRight = this.handleInstrumentMoveRight.bind(this);
    this.inputRef = React.createRef();
  }

  componentWillMount() {
    this.watchlistActions.fetchWatchlistsIfNeeded();
  }

  handleSelect(key) {
    const item = this.props.items.get(key);
    this.setState({ activeKey: key, showMap: false });
    this.graphdataActions.setItem(componentId, item.values.ITEM_SECTOR);
  }

  handleSelectMap() {
    this.setState({ showMap: true, activeKey: null });
  }

  handleClickAdd() {
    this.setState({ addItem: true });
  }

  async handleAddSelect(data) {
    this.setState({ addItem: false });
    const itemSector = `${data.ITEM}.${data.SECTOR}`;
    await this.watchlistActions.addItem(WATCHLIST_NAME, itemSector);
    this.inputRef.current.value = '';
    this.resubscribe();
  }

  handleAddCancel() {
    this.setState({ addItem: false });
    this.resubscribe();
  }

  toggleEditMode() {
    this.setState({ edit: !this.state.edit });
  }

  handleInstrumentMoveLeft(key) {
    const pos = this.props.items.get(key).values.order;
    if (pos === 0) return;
    this.handleInstrumentMove(key, pos - 1);
  }

  handleInstrumentMoveRight(key) {
    const pos = this.props.items.get(key).values.order;
    if (pos === this.props.items.size - 1) return;
    this.handleInstrumentMove(key, pos + 1);
  }

  async handleDeleteInstrument(key) {
    const { ITEM_SECTOR } = this.props.items.get(key).values;
    await this.watchlistActions.deleteItem(WATCHLIST_NAME, ITEM_SECTOR);
    this.resubscribe();
  }

  handleInstrumentMove(key, newPos) {
    const { ITEM_SECTOR } = this.props.items.get(key).values;
    this.watchlistActions.updateOrder(WATCHLIST_NAME, ITEM_SECTOR, newPos);
  }

  render() {
    const { showMap, activeKey, edit, addItem } = this.state;
    const { items } = this.props;

    let LONG_NAME;
    if (items.size > 0 && activeKey) {
      const activeItem = items.get(activeKey);
      if (activeItem) {
        LONG_NAME = activeItem.values.LONG_NAME;
      }
    }

    return (
      <Wrapper className="OverviewChart">
        <Heading>
          <h2>Min oversikt</h2>
          <button
            className="fa fa-info-circle toggle-help-text"
            title="Vis hjelpetekst"
            onClick={() => {
              this.setState({ showHelpText: !this.state.showHelpText });
            }}
          />
          <button
            onClick={this.toggleEditMode}
            aria-label="Toggle edit mode"
            className="edit"
          >
            <i className="hb-gear" />
          </button>
        </Heading>
        <HelpText open={this.state.showHelpText}>
          Viser et utvalg av de viktigste indikatorene. Denne listen kan
          redigeres ved å legge til verdipapirer som du ønsker å følge nøyere
          med på og fjerne de som ikke er relevante.
        </HelpText>
        <Body>
          <Instruments addItem={addItem}>
            <MapTab
              className={classNames({ active: showMap })}
              handleSelect={this.handleSelectMap}
            />
            {items
              .sortBy(item => item.values.order)
              .toArray()
              .map(instrument => {
                const { key } = instrument;
                return (
                  <Instrument
                    key={key}
                    handleSelect={() => this.handleSelect(key)}
                    className={classNames({ active: key === activeKey })}
                    handleMoveLeft={() => this.handleInstrumentMoveLeft(key)}
                    handleMoveRight={() => this.handleInstrumentMoveRight(key)}
                    handleDelete={() => this.handleDeleteInstrument(key)}
                    item={instrument}
                    edit={edit}
                  />
                );
              })}

            <li className="add-instrument" key="add">
              <Flipper flipped={addItem}>
                <Front>
                  <button
                    aria-label="Add instrument"
                    className="fa fa-plus-circle"
                    onClick={this.handleClickAdd}
                  />
                </Front>
                <Back>
                  <label htmlFor="newItem">Navn</label>
                  <Suggest
                    id="OverviewChartNewItem"
                    placeholder="Navn"
                    group="WATCHLIST"
                    onSelect={this.handleAddSelect}
                    innerRef={this.inputRef}
                  />
                  <button aria-label="Cancel" onClick={this.handleAddCancel}>
                    Cancel
                  </button>
                </Back>
              </Flipper>
            </li>
          </Instruments>
          <Suspense fallback={null}>
            {showMap ? (
              <div>
                <IndexMap />
              </div>
            ) : (
              <>
                <div className="clearfix">
                  <h3>{LONG_NAME}</h3>
                  <PeriodSelector />
                </div>
                <Chart name={LONG_NAME} />
              </>
            )}
          </Suspense>
        </Body>
      </Wrapper>
    );
  }
}

function mapStateToProps({ components, jaws }) {
  const componentState = components.get(componentId);

  let items = Immutable.fromJS({});
  let initialized = false;
  if (componentState) {
    items = componentState.get('items');
    initialized = componentState.get('initialized');
  }

  return {
    jaws: jaws.instance,
    items,
    initialized,
    componentId,
  };
}

export { OverviewChart, WATCHLIST_NAME, componentId };
export default connect(mapStateToProps)(OverviewChart);
